import React from "react";
import { Box, Switch } from "@chakra-ui/react";

/**
 * Glass-style card container (dark, blurred) used across stat and detail cards.
 * Forwards all extra props to the underlying Chakra Box.
 * @param {Object} props - Chakra Box props (children, style props, etc.)
 * @returns {React.ReactElement}
 */
export function StyledCard(props) {
    return (
        <Box height="100%" display="flex" flexDirection="column"
            background="rgba(30, 30, 30, 0.4)" backdropFilter="blur(20px)"
            borderRadius={{ base: "12px", sm: "16px" }}
            border="1px solid rgba(255, 255, 255, 0.15)"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.12)" transition="all 0.3s ease"
            _hover={{
                transform: "translateY(-4px)",
                background: "rgba(30, 30, 30, 0.5)",
                boxShadow: "0 12px 48px rgba(0, 0, 0, 0.18)",
                border: "1px solid rgba(255, 255, 255, 0.2)"
            }}
            {...props} />
    );
}

/**
 * Inner padded content area for StyledCard, growing to fill the card.
 * @param {Object} props - Chakra Box props
 * @returns {React.ReactElement}
 */
export function StyledCardContent(props) {
    return (
        <Box padding={{ base: "20px", sm: "24px" }} display="flex"
            flexDirection="column" flexGrow={1} {...props} />
    );
}

/**
 * Highlighted total box with a subtle gradient, used to emphasize a sum/value.
 * @param {Object} props - Chakra Box props
 * @returns {React.ReactElement}
 */
export function TotalBox(props) {
    return (
        <Box background="linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)"
            backdropFilter="blur(10px)" borderRadius="12px"
            padding={{ base: "16px", sm: "20px" }} marginTop="20px" textAlign="center"
            border="1px solid rgba(255, 255, 255, 0.08)"
            boxShadow="inset 0 1px 0 rgba(255, 255, 255, 0.05)" {...props} />
    );
}

/**
 * Row header laying out a title and trailing controls on opposite ends.
 * @param {Object} props - Chakra Box props
 * @returns {React.ReactElement}
 */
export function HeaderBox(props) {
    return (
        <Box display="flex" justifyContent="space-between" alignItems="center"
            marginBottom={{ base: "14px", sm: "16px" }} {...props} />
    );
}

/**
 * Compact toggle switch using the KantoBiz teal accent for the checked state.
 * @param {Object} props - Chakra Switch props (isChecked, onChange, etc.)
 * @returns {React.ReactElement}
 */
export function AntSwitch(props) {
    return (
        <Switch size="sm" sx={{
            "span.chakra-switch__track[data-checked]": { background: "#17dcb8" }
        }} {...props} />
    );
}
